import { useEffect, useMemo, useRef, useState } from 'react';
import { DrillSessionSummary, TREND_WINDOW, formatMs, inPlayOrder, rollingAverage } from '../drill';

/** Plot geometry. The bottom band is the date row — the box has to include it, not crop it. */
const HEIGHT = 210;
const PAD = { top: 12, right: 14, bottom: 24, left: 46 };

/** Round tick steps, in ms. Drill clocks live between a few seconds and a couple of minutes. */
const TICK_STEPS = [500, 1_000, 2_000, 2_500, 5_000, 10_000, 15_000, 20_000, 30_000, 60_000, 120_000];

function ticksFor(min: number, max: number, count = 4): number[] {
  const step = TICK_STEPS.find(s => s >= Math.max(max - min, 1) / count) ?? TICK_STEPS[TICK_STEPS.length - 1];
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max; v += step) out.push(v);
  return out;
}

const shortDate = (at: number) =>
  new Date(at).toLocaleDateString([], { month: 'short', day: 'numeric' });

/**
 * Width comes from the box, so the labels stay 11px on a phone instead of scaling down with it —
 * which is what a `viewBox` alone would do to them.
 */
function useWidth(ref: React.RefObject<HTMLDivElement | null>, initial: number): number {
  const [width, setWidth] = useState(initial);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

/**
 * Session clocks over time, with the trailing mean of the last ten across them.
 *
 * **Emphasis, not two equal series**: the average line carries the story in the accent colour and
 * the sessions themselves sit behind it as grey dots — the run-to-run scatter is real and worth
 * seeing, but it is not what the player is asking. One point per session rather than a real time
 * axis, because sessions come in clumps of an evening and a date scale would pile a week of them
 * into one pixel column and stretch a fortnight off work across the plot; the dates under the axis
 * say when the ends were, and the session list below is the exact table of the same numbers.
 */
export function SessionChart({ sessions, width: fixedWidth = 0 }: {
  sessions: DrillSessionSummary[];
  /** Only for rendering off-browser (tests, a static preview) — the box measures itself. */
  width?: number;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const width = useWidth(boxRef, fixedWidth);
  const [hover, setHover] = useState<number | null>(null);

  const runs = useMemo(() => inPlayOrder(sessions), [sessions]);
  const clocks = useMemo(() => runs.map(s => s.clockMs), [runs]);
  const trend = useMemo(() => rollingAverage(clocks), [clocks]);

  const plotW = Math.max(width - PAD.left - PAD.right, 10);
  const plotH = HEIGHT - PAD.top - PAD.bottom;

  // The domain covers the dots *and* the line, padded so neither ever sits on the frame.
  const lo = Math.min(...clocks, ...trend);
  const hi = Math.max(...clocks, ...trend);
  const pad = Math.max((hi - lo) * 0.12, 500);
  const min = Math.max(0, lo - pad), max = hi + pad;

  const x = (i: number) => PAD.left + (runs.length === 1 ? plotW / 2 : (i / (runs.length - 1)) * plotW);
  const y = (ms: number) => PAD.top + plotH - ((ms - min) / (max - min)) * plotH;

  const line = trend.map((ms, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(ms).toFixed(1)}`).join(' ');
  const last = trend[trend.length - 1];
  const best = Math.min(...clocks);

  function pick(e: React.PointerEvent<SVGSVGElement>) {
    const box = e.currentTarget.getBoundingClientRect();
    const at = e.clientX - box.left;
    const i = runs.length === 1 ? 0 : Math.round(((at - PAD.left) / plotW) * (runs.length - 1));
    setHover(Math.max(0, Math.min(runs.length - 1, i)));
  }

  const shown = hover === null ? null : runs[hover];

  return (
    <figure className="drill-chart">
      <figcaption className="drill-chart-head">
        <span className="drill-chart-title">Session times</span>
        {/* Two marks, so the key is always up: nothing here is identified by colour alone. */}
        <span className="drill-chart-legend">
          <span className="drill-key is-session" aria-hidden="true" /> session
          <span className="drill-key is-trend" aria-hidden="true" /> last {TREND_WINDOW} averaged
        </span>
      </figcaption>

      <div className="drill-chart-box" ref={boxRef}>
        {width > 0 && (
          <svg
            width={width}
            height={HEIGHT}
            role="img"
            aria-label={
              `${runs.length} sessions from ${shortDate(runs[0].at)} to ${shortDate(runs[runs.length - 1].at)}. `
              + `Best ${formatMs(best)}. Last ${TREND_WINDOW} averaged: ${formatMs(last)}.`
            }
            onPointerMove={pick}
            onPointerDown={pick}
            onPointerLeave={() => setHover(null)}
          >
            {ticksFor(min, max).map(ms => (
              <g key={ms}>
                <line className="drill-chart-grid" x1={PAD.left} x2={width - PAD.right} y1={y(ms)} y2={y(ms)} />
                <text className="drill-chart-tick" x={PAD.left - 8} y={y(ms)} textAnchor="end" dominantBaseline="middle">
                  {(ms / 1000).toFixed(0)}s
                </text>
              </g>
            ))}

            {shown && <line className="drill-chart-cursor" x1={x(hover!)} x2={x(hover!)} y1={PAD.top} y2={PAD.top + plotH} />}

            {clocks.map((ms, i) => (
              <circle key={i} className="drill-chart-dot" cx={x(i)} cy={y(ms)} r={3.5} />
            ))}

            {/* Drawn over the dots: it is the answer, they are the evidence. */}
            <path className="drill-chart-line" d={line} fill="none" />
            <circle className="drill-chart-end" cx={x(trend.length - 1)} cy={y(last)} r={4.5} />

            {hover !== null && (
              <circle className="drill-chart-dot is-on" cx={x(hover)} cy={y(clocks[hover])} r={5} />
            )}

            <text className="drill-chart-date" x={PAD.left} y={HEIGHT - 6}>{shortDate(runs[0].at)}</text>
            <text className="drill-chart-date" x={width - PAD.right} y={HEIGHT - 6} textAnchor="end">
              {shortDate(runs[runs.length - 1].at)}
            </text>
          </svg>
        )}

        {shown && (
          <div
            className="drill-chart-tip"
            style={{
              left: `${Math.min(Math.max(x(hover!), 60), Math.max(width - 60, 60))}px`,
              top: `${Math.max(y(clocks[hover!]) - 8, 0)}px`,
            }}
          >
            <strong>{formatMs(shown.clockMs)}</strong>
            <span>#{hover! + 1} · {shortDate(shown.at)}</span>
            <span>avg {formatMs(trend[hover!])}</span>
          </div>
        )}
      </div>

      <p className="drill-chart-note">
        One point per session, oldest first. The line is the mean of the last {TREND_WINDOW} —
        of everything so far, until there are {TREND_WINDOW}. Now at <strong>{formatMs(last)}</strong>,
        best single run <strong>{formatMs(best)}</strong>.
      </p>
    </figure>
  );
}
